/**
 * Supabase Realtime Presence channel per family — the single source of all
 * user-visible online/away/offline statuses (AC-14: the Cloudflare worker
 * socket is pure chat transport and never feeds status UI).
 *
 * Design (see plan Phase 3): one channel per family named
 * `presence:family:<uuid>`, presence key = the member's profile UUID. No DB
 * table and no heartbeat writes — Supabase merges multi-tab metas natively
 * and expires keys on socket loss (~heartbeat interval), which yields the
 * ≤60 s disconnect-offline guarantee (AC-10) for free.
 *
 * @module features/p2p-chat/lib/familyPresence
 */

import {
  REALTIME_SUBSCRIBE_STATES,
  type RealtimeChannel,
} from '@supabase/supabase-js';
import { getSupabaseClient } from '@/shared/clients/supabaseClient';

/** Status a member can be tracked AS (offline = absence of a tracked key). */
export type TrackablePresenceStatus = 'online' | 'away';

/** Status rendered in the UI. */
export type PresenceStatus = TrackablePresenceStatus | 'offline';

/** Map of profileId → merged presence status (absent key ⇒ offline). */
export type PresenceStatusMap = Readonly<Record<string, PresenceStatus>>;

/**
 * Merge a raw Supabase presence state into per-profile statuses using
 * "most alive wins" across metas (multiple tabs/devices per key): a profile
 * with ANY `online` meta is online; otherwise any `away` meta makes it away
 * (AC-12). Unknown/malformed payloads count as `online` (the key exists, so
 * the app is open — least surprising rendering).
 */
export function mergePresenceState(
  state: Readonly<Record<string, ReadonlyArray<Record<string, unknown>>>>
): Record<string, TrackablePresenceStatus> {
  const merged: Record<string, TrackablePresenceStatus> = {};
  for (const [profileId, metas] of Object.entries(state)) {
    if (metas.length === 0) {
      continue;
    }
    const hasOnline = metas.some((meta) => meta['status'] !== 'away');
    merged[profileId] = hasOnline ? 'online' : 'away';
  }
  return merged;
}

export interface FamilyPresenceHandle {
  /**
   * Track (or re-track) our own presence meta. Safe to call repeatedly — the
   * caller is responsible for only invoking it on state CHANGES.
   */
  trackStatus(status: TrackablePresenceStatus): void;
  /** Remove our own key (others see us offline) while staying subscribed. */
  untrack(): void;
  /** Unsubscribe and remove the channel. */
  teardown(): void;
}

export interface SubscribeFamilyPresenceOptions {
  readonly familyId: string;
  readonly profileId: string;
  /** Fired on every roster status change (sync/join/leave). */
  readonly onStatuses: (
    statuses: Record<string, TrackablePresenceStatus>
  ) => void;
  /**
   * Fired when the channel is closed underneath us (e.g. a global
   * `disconnectAllRealtime()` during family switch). The owner should
   * re-subscribe if presence is still wanted.
   */
  readonly onClosed?: () => void;
  /** Fired once the channel is joined (initial `track` can proceed). */
  readonly onReady?: () => void;
}

/**
 * Subscribe to the family presence channel. Returns a handle for tracking our
 * own status and tearing down.
 */
export function subscribeFamilyPresence(
  options: SubscribeFamilyPresenceOptions
): FamilyPresenceHandle {
  const { familyId, profileId, onStatuses, onClosed, onReady } = options;
  const client = getSupabaseClient();

  let torndown = false;
  let subscribed = false;
  /** Last status requested while the channel was still joining. */
  let pendingStatus: TrackablePresenceStatus | null = null;

  const channel: RealtimeChannel = client.channel(
    `presence:family:${familyId}`,
    { config: { presence: { key: profileId } } }
  );

  const emit = (): void => {
    onStatuses(mergePresenceState(channel.presenceState()));
  };

  channel
    .on('presence', { event: 'sync' }, emit)
    .on('presence', { event: 'join' }, emit)
    .on('presence', { event: 'leave' }, emit)
    .subscribe((status) => {
      if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
        subscribed = true;
        onReady?.();
        if (pendingStatus !== null) {
          void channel.track({ status: pendingStatus, at: nowIso() });
          pendingStatus = null;
        }
        return;
      }
      if (status === REALTIME_SUBSCRIBE_STATES.CLOSED && !torndown) {
        // Channel removed underneath us (global realtime teardown) — let the
        // owner decide whether to re-subscribe.
        subscribed = false;
        onClosed?.();
      }
    });

  return {
    trackStatus(status) {
      if (torndown) return;
      if (!subscribed) {
        pendingStatus = status;
        return;
      }
      void channel.track({ status, at: nowIso() });
    },
    untrack() {
      if (torndown || !subscribed) return;
      void channel.untrack();
    },
    teardown() {
      if (torndown) return;
      torndown = true;
      void client.removeChannel(channel);
    },
  };
}

function nowIso(): string {
  return new Date().toISOString();
}
